import {
  Box,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { ByzMuiDateTimePicker } from "src/components/core-components/mui/inputs/ByzMuiDateTimePicker";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import ByzMcParameterDetailSelect from "src/components/shared-components/ByzMcParameterDetailSelect";
import { McParameterDetailResponseDto } from "src/api/references/referenceServicesDtos";
import { useAppSelector } from "src/app/hooks";
import { useCardForm } from "src/hooks/useCardForm";
import { useTranslate } from "src/locales/use-locales";
import { useEffect, useState } from "react";
import { referenceService } from "src/api/references/referenceService";

export interface NurseApplicationFormData {
  CTIME: Date | null;
  CHOUR: Date | null;
  NON_MEDICAL_APP: string;
  ATTEMPTS_MADE: string;
  SIDE_EFFECTS: string;
  ACTIVITY_CONTROL: string;
  PAIN_SCORE: string;
  NURSE_EXPLANATION: string;
  CUSER: string;
}

interface NurseApplicationCreateProps {
  recordId: string;
  handleChange: (field: keyof NurseApplicationFormData, value: any) => void;
  onClose: () => void;
  save: (id?: string) => void;
}

function NurseApplicationCreate({
  recordId,
  handleChange: parentHandleChange,
  save,
  onClose,
}: NurseApplicationCreateProps) {
  const { user } = useAppSelector((state) => state.auth);
  const { t } = useTranslate();
  const {
    formData,
    selectedMCPs,
    loading,
    handleChange,
    handleMCPChange,
    resetForm,
    cardData,
    isEditMode,
    error,
    setError,
    getMCPParameterDetails,
    mcpCacheLoaded,
  } = useCardForm({
    cardType: "NURSE_APPLICATION",
    recordId,
    user,
    onLoadRecord: (record) => {
      Object.entries(record).forEach(([key, value]) => {
        parentHandleChange(key as keyof NurseApplicationFormData, value);
      });
    },
  });

  const [score, setScore] = useState(0);
  const typedFormData = formData as NurseApplicationFormData;

  useEffect(() => {
    if (!isEditMode) {
      resetForm();
    }
  }, [isEditMode, resetForm]);

  useEffect(() => {
    Object.entries(formData).forEach(([key, value]) => {
      parentHandleChange(key as keyof NurseApplicationFormData, value);
    });
  }, [formData, parentHandleChange]);

  const handleScoreCalculate = () => {
    const selectedValues = [
      selectedMCPs?.NON_MEDICAL_APP,
      selectedMCPs?.ATTEMPTS_MADE,
      selectedMCPs?.SIDE_EFFECTS,
      selectedMCPs?.ACTIVITY_CONTROL,
    ];
    console.log("Selected Values for Score Calculation:", selectedValues);

    const total = selectedValues.reduce((sum, item) => {
      const val = Number(item?.value ?? 0);
      return sum + val;
    }, 0);

    setScore(total);
    handleChange("PAIN_SCORE", total);
  };

  useEffect(() => {
    //handleScoreCalculate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedMCPs?.NON_MEDICAL_APP,
    selectedMCPs?.ATTEMPTS_MADE,
    selectedMCPs?.SIDE_EFFECTS,
    selectedMCPs?.ACTIVITY_CONTROL,
  ]);

  // --- PUT REQUEST ---
  const putNurseApplication = async () => {
    try {
      await referenceService.card.put({
        tablename: "MC_PAIN_ASS_SCORE_NURSE_APP",
        id: Number(recordId),
        columns: [
          { key: "NON_MEDICAL_APP", value: typedFormData.NON_MEDICAL_APP },
          { key: "ATTEMPTS_MADE", value: typedFormData.ATTEMPTS_MADE },
          { key: "SIDE_EFFECTS", value: typedFormData.SIDE_EFFECTS },
          { key: "ACTIVITY_CONTROL", value: typedFormData.ACTIVITY_CONTROL },
          { key: "PAIN_SCORE", value: score ? String(score) : String(typedFormData.PAIN_SCORE ?? "") },
          { key: "NURSE_EXPLANATION", value: typedFormData.NURSE_EXPLANATION },
        ],
      });

      onClose();
    } catch (err) {
      console.error("PUT Error:", err);
      setError({
        open: true,
        message: t("save-error") ?? "Hata oluştu.",
      });
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "200px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Snackbar
        open={!!error?.open}
        autoHideDuration={4000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setError(null)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error?.message}
        </Alert>
      </Snackbar>

      <Box sx={{ padding: "20px", width: "100%" }}>
        <Box mb={3}>
          <ByzMuiSaveButton onClick={() => putNurseApplication()} />
        </Box>

        <Grid container spacing={2}>
          <Grid
            xs={12}
            sm={6}
            md={6}
            container
            direction={"column"}
            spacing={3}
          >
            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={true}>
                {t("pain-assessment-scale.nurse-application.title")}
              </ByzMuiTypography>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("datetime")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMuiDateTimePicker
                  sx={{ width: "100%" }}
                  value={typedFormData.CTIME}
                  onChange={(date) => handleChange("CTIME", date)}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("createdby")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  color="info"
                  label={t("createdby")}
                  variant="outlined"
                  value={typedFormData.CUSER}
                  disabled
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("pain-assessment-scale.nurse-application.non-medical-app")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMcParameterDetailSelect
                  title={t(
                    "pain-assessment-scale.nurse-application.non-medical-app"
                  )}
                  mcpId={cardData?.mcpConfig?.NON_MEDICAL_APP?.mcpId ?? 0}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      handleMCPChange(
                        "NON_MEDICAL_APP",
                        val as McParameterDetailResponseDto
                      );
                    }
                  }}
                  value={selectedMCPs?.NON_MEDICAL_APP}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData?.mcpConfig?.NON_MEDICAL_APP?.mcpId ?? 0
                  )}
                  cacheLoaded={mcpCacheLoaded}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("pain-assessment-scale.nurse-application.attempts")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMcParameterDetailSelect
                  title={t("pain-assessment-scale.nurse-application.attempts")}
                  mcpId={cardData?.mcpConfig?.ATTEMPTS_MADE?.mcpId ?? 0}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      handleMCPChange(
                        "ATTEMPTS_MADE",
                        val as McParameterDetailResponseDto
                      );
                    }
                  }}
                  value={selectedMCPs?.ATTEMPTS_MADE}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData?.mcpConfig?.ATTEMPTS_MADE?.mcpId ?? 0
                  )}
                  cacheLoaded={mcpCacheLoaded}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("pain-assessment-scale.nurse-application.side-effects")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMcParameterDetailSelect
                  title={t(
                    "pain-assessment-scale.nurse-application.side-effects"
                  )}
                  mcpId={cardData?.mcpConfig?.SIDE_EFFECTS?.mcpId ?? 0}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      handleMCPChange(
                        "SIDE_EFFECTS",
                        val as McParameterDetailResponseDto
                      );
                    }
                  }}
                  value={selectedMCPs?.SIDE_EFFECTS}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData?.mcpConfig?.SIDE_EFFECTS?.mcpId ?? 0
                  )}
                  cacheLoaded={mcpCacheLoaded}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t(
                    "pain-assessment-scale.nurse-application.activity-control"
                  )}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMcParameterDetailSelect
                  title={t("pain-assessment-scale.nurse-application.activity-control")}
                  mcpId={cardData?.mcpConfig?.ACTIVITY_CONTROL?.mcpId ?? 0}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      handleMCPChange(
                        "ACTIVITY_CONTROL",
                        val as McParameterDetailResponseDto
                      );
                    }
                  }}
                  value={selectedMCPs?.ACTIVITY_CONTROL}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData?.mcpConfig?.ACTIVITY_CONTROL?.mcpId ?? 0
                  )}
                  cacheLoaded={mcpCacheLoaded}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("pain-assessment-scale.nurse-application.score")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  color="info"
                  variant="outlined"
                  value={typedFormData.PAIN_SCORE}
                  onChange={(e) => handleChange("PAIN_SCORE", e.target.value)}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t(
                    "pain-assessment-scale.nurse-application.nurse-explanation"
                  )}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  color="info"
                  variant="outlined"
                  value={typedFormData.NURSE_EXPLANATION}
                  onChange={(e) =>
                    handleChange("NURSE_EXPLANATION", e.target.value)
                  }
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  );
}

export default NurseApplicationCreate;
